import React, { createContext, useState, useEffect, ReactNode } from 'react';

import { categories, adminCategories, Category } from '../shared/components/bars/side-bar/components/SideBarCategories';

const isSysAdmin = (): boolean => {
  try { return localStorage.getItem('is_sys_admin') === 'true'; }
  catch { return false; }
};

const getInitialCategories = (): Category[] =>
  isSysAdmin() ? [...categories, ...adminCategories] : [...categories];

interface NavigationProviderProps {
  children: ReactNode;
}

type NavigationContextType = {
  categoryList: Category[];
  addSubcategory: (parentId: string, newSubcategory: Category) => void;
};

export const NavigationContext = createContext<NavigationContextType>({
  categoryList: [],
  addSubcategory: () => {},
});

export const NavigationProvider: React.FC<NavigationProviderProps & { isAuthenticated: boolean }> = ({
  children,
  isAuthenticated,
}) => {
  const [categoryList, setCategoryList] = useState<Category[]>(getInitialCategories());

  // The sidebar is fully static now — its items no longer come from the backend.
  // (The old code fetched get_user_preference_left_menu to inject "AI Generated"
  // reports and "Roadmaps" boards, but those sections are commented out in the
  // streamlined nav, so the fetch attached nothing while BLOCKING the whole app
  // render until it resolved.) We just refresh from localStorage on auth change
  // so the admin section appears for sys-admins. No backend call, no render gate.
  useEffect(() => {
    setCategoryList(getInitialCategories());
  }, [isAuthenticated]);

  const findAndAddChildren = (nodes: Category[], targetID: string, kids: Category[]): Category[] =>
    nodes.map((node) => {
      if (node.id === targetID) {
        node.children = node.children ? node.children.concat(kids) : kids;
        return node;
      }
      if (node.children) {
        node.children = findAndAddChildren(node.children, targetID, kids);
      }
      return node;
    });

  const addSubcategory = (parentId: string, newSubcategory: Category) => {
    setCategoryList((current) => findAndAddChildren(current, parentId, [newSubcategory]));
  };

  return (
    <NavigationContext.Provider value={{ categoryList, addSubcategory }}>
      {children}
    </NavigationContext.Provider>
  );
};
